import type { SponsoredBrandAsset, SponsoredDisplayCapabilities } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";
import { AssetPicker } from "./AssetPicker";
import { CreativePreview } from "./CreativePreview";
import { cropFor } from "./creative-assets";
import {
  CREATIVE_PREVIEW_PAGE_COUNT,
  CREATIVE_PREVIEW_PLACEMENTS,
  type CreativeDraft,
  type CreativePreviewPlacement,
} from "./sponsored-display-draft";

export function CreativeEditor({
  draft,
  products,
  capabilities,
  assetsLoading,
  assetsUnavailable,
  logoAssets,
  imageAssets,
  onUpdate,
}: {
  draft: CreativeDraft;
  products: SelectedProduct[];
  capabilities: SponsoredDisplayCapabilities["creative"];
  assetsLoading: boolean;
  assetsUnavailable: boolean;
  logoAssets: SponsoredBrandAsset[];
  imageAssets: SponsoredBrandAsset[];
  onUpdate: (patch: Partial<CreativeDraft>) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const previewPageCount = Math.min(CREATIVE_PREVIEW_PAGE_COUNT, Math.max(1, products.length));
  // A restored draft, or a shrunken product selection, can carry a page beyond
  // the current selection. Clamp for this render and write the clamp back so the
  // persisted draft cannot resurrect the stale page when products are re-added.
  const previewPage = Math.min(draft.previewPage, previewPageCount);
  const product = products[previewPage - 1] ?? null;
  useEffect(() => {
    if (draft.previewPage > previewPageCount) onUpdate({ previewPage: previewPageCount });
  }, [draft.previewPage, previewPageCount, onUpdate]);
  const logo = logoAssets.find((asset) => asset.assetId === draft.logoAssetId) ?? null;
  const image = imageAssets.find((asset) => asset.assetId === draft.imageAssetId) ?? null;
  const allThree = draft.logoEnabled && draft.headlineEnabled && draft.imageEnabled;
  const placementItems = CREATIVE_PREVIEW_PLACEMENTS.map((value) => ({
    value,
    label: t(`sd.creative.preview.placements.${value}`),
  }));
  return (
    <Collapsible
      open={draft.expanded}
      onOpenChange={(open) => onUpdate({ expanded: open })}
      data-slot="sd-ad-preview"
      data-placement={draft.placement}
      data-page={previewPage}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-6 py-3">
        <CollapsibleTrigger render={<Button type="button" />}>
          {t("sd.creative.edit")}
          <IconChevronDown className={cn("transition-transform", draft.expanded && "rotate-180")} />
        </CollapsibleTrigger>
        <Select
          items={placementItems}
          value={draft.placement}
          onValueChange={(value) => {
            if (value) onUpdate({ placement: value as CreativePreviewPlacement });
          }}
        >
          <SelectTrigger aria-label={t("sd.creative.preview.placementLabel")} className="min-w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {placementItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t("sd.creative.preview.previousPage")}
          disabled={previewPage === 1}
          onClick={() => onUpdate({ previewPage: Math.max(1, previewPage - 1) })}
        >
          <IconChevronLeft data-icon="inline-start" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t("sd.creative.preview.nextPage")}
          disabled={previewPage === previewPageCount}
          onClick={() =>
            onUpdate({
              previewPage: Math.min(previewPageCount, previewPage + 1),
            })
          }
        >
          <IconChevronRight data-icon="inline-start" />
        </Button>
        <output className="font-medium text-muted-foreground" aria-live="polite">
          {t("sd.creative.preview.pagination", {
            page: previewPage,
            total: previewPageCount,
          })}
        </output>
      </div>
      {capabilities.dynamic.state !== "supported" &&
      !draft.logoEnabled &&
      !draft.headlineEnabled &&
      !draft.imageEnabled ? (
        <Alert className="mx-6 mt-4" variant="warning">
          <IconAlertTriangle />
          <AlertTitle>{t("sd.creative.dynamicUnsupported")}</AlertTitle>
          <AlertDescription>{t("sd.creative.dynamicUnsupportedDescription")}</AlertDescription>
        </Alert>
      ) : null}
      {!draft.expanded ? (
        <div className="px-6 py-4">
          <CreativePreview
            headline={draft.headlineEnabled ? draft.headline : ""}
            logo={draft.logoEnabled ? logo : null}
            image={draft.imageEnabled ? image : null}
            placement={draft.placement}
            product={product}
          />
        </div>
      ) : null}
      <CollapsiblePanel>
        <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-6 px-6 py-4">
          <div className="flex flex-col gap-5">
            <p className="text-sm text-muted-foreground">{t("sd.creative.helper")}</p>
            <CreativeOption
              id="logo"
              checked={draft.logoEnabled}
              disabled={capabilities.logo.state !== "supported"}
              label={t("sd.creative.logo.title")}
              onCheckedChange={(checked) => onUpdate({ logoEnabled: checked })}
            >
              <RequirementList
                title={t("sd.creative.logo.requirements")}
                items={["dimensions", "size", "format"].map((key) => t(`sd.creative.logo.${key}`))}
              />
              <AssetPicker
                assets={logoAssets}
                loading={assetsLoading}
                unavailable={assetsUnavailable}
                value={draft.logoAssetId}
                onValueChange={(assetId) => onUpdate({ logoAssetId: assetId })}
              />
              {logo ? <CropSummary asset={logo} sizes={[[600, 100]]} /> : null}
            </CreativeOption>
            <CreativeOption
              id="headline"
              checked={draft.headlineEnabled}
              disabled={capabilities.headline.state !== "supported"}
              label={t("sd.creative.headline.title")}
              onCheckedChange={(checked) => onUpdate({ headlineEnabled: checked })}
            >
              <Field data-invalid={draft.headline.length > 50}>
                <div className="flex items-center justify-between">
                  <FieldLabel htmlFor="sd-headline">{t("sd.creative.headline.title")}</FieldLabel>
                  <span className="font-mono text-xs text-muted-foreground">
                    {draft.headline.length}/50
                  </span>
                </div>
                <Textarea
                  id="sd-headline"
                  maxLength={50}
                  value={draft.headline}
                  onChange={(event) => onUpdate({ headline: event.target.value })}
                />
                <FieldDescription>
                  <a
                    className="text-primary underline-offset-4 hover:underline"
                    href="https://advertising.amazon.com/resources/ad-policy/creative-acceptance#editorialguidelines"
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t("sd.creative.headline.policy")}
                  </a>
                </FieldDescription>
              </Field>
            </CreativeOption>
            <CreativeOption
              id="image"
              checked={draft.imageEnabled}
              disabled={capabilities.image.state !== "supported"}
              label={t("sd.creative.image.title")}
              onCheckedChange={(checked) => onUpdate({ imageEnabled: checked })}
            >
              <RequirementList
                title={t("sd.creative.image.requirements")}
                items={["dimensions", "size", "format", "content"].map((key) =>
                  t(`sd.creative.image.${key}`),
                )}
              />
              <AssetPicker
                assets={imageAssets}
                loading={assetsLoading}
                unavailable={assetsUnavailable}
                value={draft.imageAssetId}
                onValueChange={(assetId) => onUpdate({ imageAssetId: assetId })}
              />
              {image ? (
                <CropSummary
                  asset={image}
                  sizes={[
                    [1200, 628],
                    [628, 628],
                  ]}
                />
              ) : null}
            </CreativeOption>
            {allThree ? (
              <Alert variant="warning">
                <IconAlertTriangle />
                <AlertDescription>{t("sd.creative.warning.description")}</AlertDescription>
              </Alert>
            ) : null}
          </div>
          <div>
            <h3 className="mb-3 font-medium">{t("sd.creative.preview.title")}</h3>
            <CreativePreview
              headline={draft.headlineEnabled ? draft.headline : ""}
              logo={draft.logoEnabled ? logo : null}
              image={draft.imageEnabled ? image : null}
              placement={draft.placement}
              product={product}
            />
          </div>
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

function CreativeOption({
  id,
  checked,
  disabled,
  label,
  onCheckedChange,
  children,
}: {
  id: "logo" | "headline" | "image";
  checked: boolean;
  disabled: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("campaignCreation");
  const controlId = `sd-creative-${id}`;
  return (
    <section className="rounded-lg border border-line">
      <label
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        htmlFor={controlId}
      >
        <span className="font-medium">{label}</span>
        <span className="flex items-center gap-2">
          {disabled ? <Badge variant="outline">{t("sd.unsupported")}</Badge> : null}
          <Switch
            id={controlId}
            checked={checked}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
          />
        </span>
      </label>
      {checked ? (
        <div className="flex flex-col gap-4 border-t border-line p-4">{children}</div>
      ) : null}
    </section>
  );
}

function RequirementList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <p className="text-sm font-medium">{title}</p>
      <ul className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>- {item}</li>
        ))}
      </ul>
    </div>
  );
}

function CropSummary({
  asset,
  sizes,
}: {
  asset: SponsoredBrandAsset;
  sizes: Array<readonly [number, number]>;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="grid grid-cols-2 gap-3">
      {sizes.map(([width, height]) => {
        const crop = cropFor(asset, width / height);
        return (
          <div className="rounded-lg border border-line p-3" key={`${width}-${height}`}>
            <p className="text-sm font-medium">{t("sd.creative.cropImage", { width, height })}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              x {crop.left}, y {crop.top}
            </p>
          </div>
        );
      })}
    </div>
  );
}
